import React, { useId } from 'react';
type Wavelength = '460' | '665' | '850' | '1064';
const bands: {
  key: Wavelength;
  label: string;
  color: string;
  glow: string;
  reach: number;
}[] = [{
  key: '460',
  label: 'Blue LED',
  color: '#78B5FF',
  glow: '#3475E5',
  reach: .34
}, {
  key: '665',
  label: 'Red laser',
  color: '#FF8B7C',
  glow: '#D34C46',
  reach: .64
}, {
  key: '850',
  label: 'NIR laser',
  color: '#BDA4F2',
  glow: '#8663C8',
  reach: .93
}, {
  key: '1064',
  label: 'NIR laser',
  color: '#E7BD6B',
  glow: '#BE8C3E',
  reach: .93
}];

/** A conceptual tissue illustration; beam fades are not measured penetration depths. */
export function SkinLightDiagram({
  selected,
  compact = false
}: {
  selected?: Wavelength;
  compact?: boolean;
}) {
  const uid = `skin-${useId().replace(/:/g, '')}`;
  const width = compact ? 470 : 1120;
  const height = compact ? 390 : 540;
  const left = compact ? 26 : 42;
  const right = compact ? 288 : 872;
  const side = compact ? 18 : 32;
  const top = compact ? 102 : 150;
  const epi = compact ? 145 : 211;
  const dermis = compact ? 265 : 374;
  const bottom = compact ? 356 : 492;
  const labelX = compact ? 320 : 945;
  const labelSize = compact ? 20 : 25;
  const surface = `M${left} ${top} C${left + (right - left) * .15} ${top - 9} ${left + (right - left) * .22} ${top + 6} ${left + (right - left) * .34} ${top} S${left + (right - left) * .58} ${top - 5} ${left + (right - left) * .7} ${top} S${right - 24} ${top + 4} ${right} ${top}`;
  const tissue = `${surface} V${bottom} H${left} Z`;
  const beamBands = selected ? bands.filter(band => band.key === selected) : bands;
  const beamX = (index: number) => selected ? (left + right) / 2 : left + (right - left) * ((index + .5) / 4);
  const key = (name: string) => `${uid}-${name}`;
  const url = (name: string) => `url(#${key(name)})`;
  const epiCenter = (top + epi) / 2;
  const dermisCenter = (epi + dermis) / 2;
  const fatCenter = (dermis + bottom) / 2;
  return <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Conceptual skin cross-section showing light fading through the epidermis, dermis and subcutaneous tissue${selected ? ` for ${selected} nanometers` : ' at four wavelengths'}.`} className="block h-auto w-full" data-skin-light-diagram={compact ? 'compact' : 'desktop'} data-id="element-319" data-mp="y18dbcicg21w">
      <defs data-id="element-320" data-mp="2lxpnwx0sxcjp">
        <linearGradient id={key('epidermis')} x1="0" x2="0" y1="0" y2="1" data-id="element-321" data-mp="2lxzheul221u5">
          <stop stopColor="#F1C4AB" data-id="element-322" data-mp="2hvunduqp336z" /><stop offset="1" stopColor="#D99378" data-id="element-323" data-mp="2hw4fpoz7pmfu" />
        </linearGradient>
        <linearGradient id={key('dermis')} x1="0" x2="0" y1="0" y2="1" data-id="element-324" data-mp="2cwrj4cijh102">
          <stop stopColor="#F6CBB9" data-id="element-325" data-mp="2cvghai8nm3bl" /><stop offset="1" stopColor="#E6AA98" data-id="element-326" data-mp="27xoprpbx8j7x" />
        </linearGradient>
        <linearGradient id={key('fat')} x1="0" x2="0" y1="0" y2="1" data-id="element-327" data-mp="27zrb3w24po6b">
          <stop stopColor="#EED39D" data-id="element-328" data-mp="22zviesyvtj62" /><stop offset="1" stopColor="#DDBD80" data-id="element-329" data-mp="230es5medp190" />
        </linearGradient>
        <linearGradient id={key('side')} x1="0" x2="1" data-id="element-330" data-mp="650hv2oc17lt">
          <stop stopColor="#AD755B" data-id="element-331" data-mp="17gqr26jd0cg" /><stop offset="1" stopColor="#D4A88A" data-id="element-332" data-mp="16yuw6nsco52" />
        </linearGradient>
        <linearGradient id={key('top-face')} x1="0" x2="0" y1="0" y2="1" data-id="element-333" data-mp="9ohd83ooe7y4">
          <stop stopColor="#F8DFCB" data-id="element-334" data-mp="9m4wftunm3mn" /><stop offset="1" stopColor="#E3AF91" data-id="element-335" data-mp="dpkv0vqaae1z" />
        </linearGradient>
        <filter id={key('soft-glow')} x="-100%" y="-25%" width="300%" height="150%" data-id="element-336" data-mp="dngg7iqujumx">
          <feGaussianBlur stdDeviation={compact ? 8 : 13} data-id="element-337" data-mp="ihb5r2t4v3tx" />
        </filter>
        <filter id={key('shadow')} x="-15%" y="-50%" width="140%" height="200%" data-id="element-338" data-mp="neb4strkf6bj">
          <feGaussianBlur stdDeviation={compact ? 7 : 12} data-id="element-339" data-mp="3mvj1zb8loyqr" />
        </filter>
        <clipPath id={key('tissue')} data-id="element-340" data-mp="3qw30ai4wu8eh"><path d={tissue} data-id="element-341" data-mp="3qxe3afqj7c4j" /></clipPath>
        <clipPath id={key('epidermis-clip')} data-id="element-342" data-mp="3qyfh2ryxmcpy"><path d={`${surface} V${epi} H${left} Z`} data-id="element-343" data-mp="3vuwgiibl0dic" /></clipPath>
        <clipPath id={key('dermis-clip')} data-id="element-344" data-mp="3vxiouk6aqx1m"><rect x={left} y={epi} width={right - left} height={dermis - epi} data-id="element-345" data-mp="4ouy1opaehmo" /></clipPath>
        <clipPath id={key('fat-clip')} data-id="element-346" data-mp="1b57226esitq6"><rect x={left} y={dermis} width={right - left} height={bottom - dermis} data-id="element-347" data-mp="1b5zzuhvru8w6" /></clipPath>
        <pattern id={key('surface-cells')} width={compact ? 43 : 67} height={compact ? 16 : 23} patternUnits="userSpaceOnUse" data-id="element-348" data-mp="1g3id2kgtna9z">
          <ellipse cx={compact ? 21 : 33} cy={compact ? 8 : 11} rx={compact ? 18 : 29} ry={compact ? 5.3 : 8} fill="#F7D8C5" fillOpacity=".22" stroke="#AD705B" strokeWidth=".9" strokeOpacity=".28" data-id="element-349" data-mp="2p1rxtzuyjfpp" />
        </pattern>
        {bands.map(band => {
        const end = top + (bottom - top) * band.reach;
        return <linearGradient key={band.key} id={key(`beam-${band.key}`)} gradientUnits="userSpaceOnUse" x1="0" x2="0" y1={top - 35} y2={end} data-id="element-350" data-mp="2t56bijvfbjzz">
              <stop stopColor={band.color} stopOpacity=".84" data-id="element-351" data-mp="32zyc7wkaymyz" />
              <stop offset=".28" stopColor={band.color} stopOpacity=".7" data-id="element-352" data-mp="3gsehl40oex32" />
              <stop offset=".7" stopColor={band.glow} stopOpacity=".27" data-id="element-353" data-mp="3guikpjqr3s2a" />
              <stop offset="1" stopColor={band.glow} stopOpacity="0" data-id="element-354" data-mp="3cs41gyj66qah" />
            </linearGradient>;
      })}
      </defs>

      <g aria-hidden="true" data-id="element-355" data-mp="3qo96ggvhtoea">
        <ellipse cx={(left + right + side) / 2} cy={bottom + 14} rx={(right - left) * .47} ry={compact ? 11 : 16} fill="#8C694B" opacity=".13" filter={url('shadow')} data-id="element-356" data-mp="3ls0x5k3z8pf1" />
        <path d={`M${right} ${top} l${side} -17 V${bottom - 17} l-${side} 17 Z`} fill={url('side')} data-id="element-357" data-mp="e6zot5a1zl91" />
        <path d={`${surface} l${side} -17 H${left + side} Z`} fill={url('top-face')} data-id="element-358" data-mp="3vf4w573eum6l" />
        <g clipPath={url('tissue')} data-id="element-359" data-mp="3vhh9t824yadu">
          <path d={`${surface} V${epi} H${left} Z`} fill={url('epidermis')} data-id="element-360" data-mp="480pwef3b3z8" />
          <rect x={left} y={epi} width={right - left} height={dermis - epi} fill={url('dermis')} data-id="element-361" data-mp="3mmt2zdgwwkmx" />
          <rect x={left} y={dermis} width={right - left} height={bottom - dermis} fill={url('fat')} data-id="element-362" data-mp="3rdjd140a9bny" />
          <g clipPath={url('epidermis-clip')} data-id="element-363" data-mp="3covhuyucn3yj">
            <rect x={left} y={top - 8} width={right - left} height={epi - top + 10} fill={url('surface-cells')} data-id="element-364" data-mp="3cq6mf0vmcfqp" />
          </g>
          <g clipPath={url('dermis-clip')} strokeLinecap="round" fill="none" data-id="element-365" data-mp="3hllu1b319feo">
            {Array.from({
            length: compact ? 13 : 25
          }, (_, i) => {
            const x = left - 45 + i * (compact ? 28 : 39);
            const y = epi + 22 + i % 4 * (compact ? 22 : 32);
            return <g key={i} data-id="element-366" data-mp="1e8mng9ptxxlk">
                  <path d={`M${x} ${y} c24 -23 35 25 66 4 s39 -22 69 1`} stroke="#B87573" strokeOpacity=".29" strokeWidth={compact ? 1.2 : 1.5} data-id="element-367" data-mp="19fl51rqezfx8" />
                  <path d={`M${x + 11} ${y + 8} c24 -23 35 25 66 4 s39 -22 69 1`} stroke="#FFF1E4" strokeOpacity=".65" strokeWidth={compact ? 1.1 : 1.6} data-id="element-368" data-mp="14hjk119fh6j4" />
                </g>;
          })}
            {Array.from({
            length: compact ? 8 : 17
          }, (_, i) => <ellipse key={i} cx={left + 18 + i * (right - left - 30) / (compact ? 7 : 16)} cy={epi + 28 + i * 31 % (dermis - epi - 44)} rx={compact ? 4 : 5.5} ry={compact ? 1.8 : 2.1} fill="#A3666A" fillOpacity=".29" stroke="none" transform={`rotate(-24 ${left + 18 + i * (right - left - 30) / (compact ? 7 : 16)} ${epi + 28 + i * 31 % (dermis - epi - 44)})`} data-id="element-369" data-mp="1s6b578oloome" />)}
          </g>
          <g clipPath={url('fat-clip')} data-id="element-370" data-mp="7pg0y60viara">
            {Array.from({
            length: 3
          }, (_, row) => Array.from({
            length: compact ? 7 : 17
          }, (_, column) => {
            const r = compact ? 26 : 32;
            const x = left + column * r * 1.68 + (row % 2 ? r * .84 : 0);
            const y = dermis + row * r * 1.48 + 8;
            return <ellipse key={`${row}-${column}`} cx={x} cy={y} rx={r} ry={r * .82} fill="#F8E6B9" fillOpacity=".56" stroke="#BC9659" strokeWidth={compact ? 1.3 : 1.7} strokeOpacity=".49" data-id="element-371" data-mp="2x5hlzo473xw5" />;
          }))}
          </g>
          <path d={`M${left} ${epi} Q${(left + right) / 2} ${epi - 5} ${right} ${epi}`} fill="none" stroke="#B77F69" strokeOpacity=".4" strokeWidth="1.4" data-id="element-372" data-mp="3735rn93capt3" />
          <path d={`M${left} ${dermis} Q${(left + right) / 2} ${dermis + 5} ${right} ${dermis}`} fill="none" stroke="#B38A60" strokeOpacity=".45" strokeWidth="1.5" data-id="element-373" data-mp="3b626qpto83sa" />
          {beamBands.map((band, i) => {
          const x = beamX(i);
          const end = top + (bottom - top) * band.reach;
          const beamWidth = compact ? 70 : 78;
          return <g key={band.key} data-skin-beam={band.key} data-id="element-374" data-mp="3nmjyajnbs6il" data-mpi={i}>
                <path d={`M${x - beamWidth * .42} ${top - 14} H${x + beamWidth * .42} L${x + beamWidth * .78} ${end} H${x - beamWidth * .78} Z`} fill={url(`beam-${band.key}`)} filter={url('soft-glow')} data-id="element-375" data-mp="3jmj7dzj1i2un" />
                <path d={`M${x - beamWidth * .2} ${top - 14} H${x + beamWidth * .2} L${x + beamWidth * .35} ${end} H${x - beamWidth * .35} Z`} fill={url(`beam-${band.key}`)} data-id="element-376" data-mp="3sebfi3klczw2" />
                <path d={`M${x} ${top - 14} V${end}`} stroke={url(`beam-${band.key}`)} strokeWidth={compact ? 3 : 4} data-id="element-377" data-mp="30o6xuco7uqyj" />
                {[-1, 1].map(direction => <path key={direction} d={`M${x + direction * 5} ${top + 15} C${x + direction * 14} ${top + (end - top) * .35} ${x + direction * 39} ${top + (end - top) * .56} ${x + direction * 65} ${end}`} fill="none" stroke={url(`beam-${band.key}`)} strokeWidth="2.2" opacity=".43" data-id="element-378" data-mp="3ektx4m3n5t7m" />)}
              </g>;
        })}
        </g>
        <path d={surface} fill="none" stroke="#B97E61" strokeWidth="2" data-id="element-379" data-mp="26xu5ffrhb254" />
        <path d={`M${left} ${top + 3} V${bottom} H${right}`} fill="none" stroke="#B7886D" strokeOpacity=".38" strokeWidth="1.2" data-id="element-380" data-mp="2bw5dpm24l4l8" />
      </g>

      {beamBands.map((band, i) => {
      const x = beamX(i);
      return <g key={band.key} data-id="element-381" data-mp="22vqssnt2pri9" data-mpi={i}>
            {!compact && <>
              <text x={x} y="36" textAnchor="middle" fill="#332D29" fontSize="30" fontFamily="inherit" fontWeight="500" data-id="element-382" data-mp="2qkru7r3ho2e1">{band.key}<tspan fontSize="19" data-id="element-383" data-mp="2ukaeqlreebgc"> nm</tspan></text>
              <text x={x} y="66" textAnchor="middle" fill="#6A6057" fontSize="22" fontFamily="inherit" data-id="element-384" data-mp="2gu70agyrfm1g">{band.label}</text>
            </>}
            <g aria-hidden="true" data-id="element-385" data-mp="2lr77iv7ciuu5">
              <path d={`M${x} ${compact ? 48 : 92} V${top - 4}`} stroke={band.color} strokeWidth={compact ? 15 : 12} opacity=".25" filter={url('soft-glow')} data-id="element-386" data-mp="39iclf4tq7d1t" />
              <path d={`M${x} ${compact ? 48 : 92} V${top - 4}`} stroke={band.glow} strokeWidth="3" strokeLinecap="round" opacity=".77" data-id="element-387" data-mp="3eam6a6fzhmx8" />
              <ellipse cx={x} cy={top - 1} rx={compact ? 22 : 24} ry="5" fill={band.color} opacity=".62" data-id="element-388" data-mp="2fevcuv7u6alh" />
              <circle cx={x} cy={compact ? 43 : 86} r={compact ? 7 : 6} fill={band.color} stroke={band.glow} strokeWidth="1.2" data-id="element-389" data-mp="21f40t00vr4a4" />
            </g>
          </g>;
    })}

      <g fill="#51473D" fontFamily="inherit" fontSize={labelSize} data-id="element-390" data-mp="25isappczibyj">
        <path d={`M${right + side + 7} ${epiCenter} H${labelX - 11} M${right + side + 7} ${dermisCenter} H${labelX - 11} M${right + side + 7} ${fatCenter} H${labelX - 11}`} fill="none" stroke="#AD9D87" strokeWidth="1.2" aria-hidden="true" data-id="element-391" data-mp="1roiwcvyiz5ja" />
        <text x={labelX} y={epiCenter + 7} data-id="element-392" data-mp="1mq7nang20z24">Epidermis</text>
        <text x={labelX} y={dermisCenter + 7} data-id="element-393" data-mp="1mqr39ym59ddi">Dermis</text>
        <text x={labelX} y={fatCenter - (compact ? 1 : 3)} data-id="element-394" data-mp="18pww1jau1igf">
          <tspan x={labelX} data-id="element-395" data-mp="18sjbe53yt297">Subcutaneous</tspan><tspan x={labelX} dy={compact ? 25 : 29} data-id="element-396" data-mp="1dq1al3s6k340">tissue</tspan>
        </text>
      </g>
    </svg>;
}